import posthog from 'posthog-js';

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;

export function initAnalytics() {
  if (!POSTHOG_KEY) return;
  var authData = localStorage.getItem('authData');
  var isAdmin = false;
  try {
    if (authData) {
      var parsed = JSON.parse(authData);
      isAdmin = !!(parsed && parsed.user && parsed.user.isAdmin);
    }
  } catch (e) {}
  posthog.init(POSTHOG_KEY, {
    api_host: 'https://us.i.posthog.com',
    capture_pageview: false,
    disable_session_recording: isAdmin,
    autocapture: true,
  });
}

export function identifyUser(userId, properties) {
  if (!POSTHOG_KEY) return;
  posthog.identify(userId, properties);
}

export function resetUser() {
  if (!POSTHOG_KEY) return;
  posthog.reset();
}

export function trackEvent(name, properties) {
  if (!POSTHOG_KEY) return;
  posthog.capture(name, properties);
}

/**
 * Maps an HTTP status onto a stable, low-cardinality bucket so auth failures
 * group cleanly in PostHog even when the server's `reason` wording changes.
 */
function classifyStatus(status) {
  if (typeof status !== 'number') return 'network_error';
  if (status === 400) return 'bad_request';
  if (status === 401) return 'invalid_credentials';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status === 429) return 'rate_limited';
  if (status >= 500) return 'server_error';
  return 'http_' + status;
}

/**
 * Reports a failure in the login or signup funnel. Every branch that shows the
 * user an error calls this, so a drop-off in the funnel has a cause attached
 * rather than just disappearing. Sentry deliberately ignores 4xx, so without
 * this a "username taken" or a throttled login is recorded nowhere at all.
 *
 * Pass `error` for an axios rejection (status and bucket are derived from it),
 * or `code` for a client-side validation branch that never hit the network.
 *
 * Group on `code`, never on `reason`. `reason` is whatever text the server
 * sent and is unbounded by design: the throttle message interpolates a wait
 * time, and social-login echoes the submitted `provider` back. It is truncated
 * here so an oversized body cannot become an enormous property, but callers
 * must still keep emails, passwords, and tokens out of it, since these events
 * fire before identify().
 *
 * @param {string} step   Funnel stage, e.g. 'login', 'choose_username'.
 * @param {object} [details]
 * @param {string} [details.method]  'email' | 'google' | 'apple'
 * @param {object} [details.error]   axios error, used to derive status
 * @param {string} [details.code]    explicit bucket for client-side validation
 * @param {string} [details.reason]  the message actually shown to the user
 * @param {string} [details.detail]  extra low-cardinality context, e.g. which
 *                                   checkbox was left unticked
 */
export function trackAuthError(step, details) {
  var opts = details || {};
  var status = opts.error && opts.error.response ? opts.error.response.status : opts.status;
  var isServer = typeof status === 'number';
  trackEvent('auth_error', {
    step: step,
    method: opts.method || 'unknown',
    code: opts.code || classifyStatus(status),
    status: isServer ? status : null,
    reason: opts.reason ? String(opts.reason).slice(0, 200) : null,
    detail: opts.detail || null,
    source: isServer ? 'server' : 'client',
  });
}

export function trackPageView() {
  if (!POSTHOG_KEY) return;
  posthog.capture('$pageview');
}

export function getFeatureFlag(key) {
  if (!POSTHOG_KEY) return undefined;
  return posthog.getFeatureFlag(key);
}

export function onFeatureFlags(callback) {
  if (!POSTHOG_KEY) return function () {};
  return posthog.onFeatureFlags(callback);
}
